import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      // 401 is expected when not signed in; log others for debugging
      if (err?.response?.status !== 401) console.debug('auth/me failed', err);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.debug('logout request failed', err);
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, login, logout, isPro: !!user?.is_pro, isFreehandPro: !!user?.is_freehand_pro, isAdmin: !!user?.is_admin }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function AuthCallback() {
  const { refresh } = useAuth();
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    const sessionId = match[1];
    (async () => {
      try {
        await axios.post(`${API}/auth/session`, { session_id: sessionId });
        await refresh();
      } finally {
        window.history.replaceState({}, '', '/');
        window.location.reload();
      }
    })();
  }, [refresh]);
  return (
    <div className="flex items-center justify-center h-screen text-slate-600" data-testid="auth-callback">
      Signing you in…
    </div>
  );
}
